/**
 * Shared address history — synced with the server so the whole team benefits.
 *
 * - `pushAddressHistory(place)`: upsert on server (fire-and-forget)
 * - `getCachedAddressHistory()`: sync accessor — reads local cache
 * - `refreshAddressHistory()`: fetches latest server list, updates cache
 * - `removeAddressHistory(addr)` / `clearAddressHistory()`: remote + local
 * - `filterCachedAddressHistory(q)`: sync filter on the cached list
 *
 * Falls back to localStorage-only when the server is unreachable (e.g. old
 * production before the endpoint was deployed).
 */
import api from "@/lib/api";

const CACHE_KEY = "bkpro_address_history_v2";
const MAX = 10; // shown to user; server keeps 40

let _cache = null;
let _lastFetch = 0;
const TTL_MS = 30_000;

function _readLocal() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch (e) { console.debug("[address-history] readLocal parse failed:", e?.message || e); return []; }
}

function _writeLocal(list) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(list.slice(0, MAX))); }
  catch (e) { console.debug("[address-history] writeLocal blocked (private mode / quota):", e?.message || e); }
}

function _sortByTs(arr) {
  return [...arr].sort((a, b) => {
    const ta = new Date(a.ts || 0).getTime();
    const tb = new Date(b.ts || 0).getTime();
    return tb - ta;
  });
}

export async function refreshAddressHistory() {
  try {
    const r = await api.get("/address-history");
    const list = _sortByTs(r.data || []).slice(0, MAX);
    _cache = list;
    _lastFetch = Date.now();
    _writeLocal(list);
    return list;
  } catch (e) {
    console.debug("[address-history] refresh failed, falling back to local cache:", e?.message || e);
    _cache = _readLocal();
    return _cache;
  }
}

export function getCachedAddressHistory() {
  if (_cache) return _cache;
  _cache = _readLocal();
  // Background refresh if stale
  if (Date.now() - _lastFetch > TTL_MS) {
    void refreshAddressHistory();
  }
  return _cache;
}

export async function pushAddressHistory(place) {
  if (!place || !place.address) return;
  // Optimistic local update
  const now = new Date().toISOString();
  const entry = {
    address: place.address,
    name: place.name || place.address,
    label: place.label || place.address,
    postal_code: place.postal_code || null,
    city: place.city || null,
    latitude: place.latitude ?? null,
    longitude: place.longitude ?? null,
    category: place.category || null,
    ts: now,
  };
  const key = (place.address || "").toLowerCase().trim();
  const current = _cache || _readLocal();
  const next = [entry, ...current.filter((p) => (p.address || "").toLowerCase().trim() !== key)].slice(0, MAX);
  _cache = next;
  _writeLocal(next);
  // Fire-and-forget remote upsert
  try { await api.post("/address-history", entry); } catch (e) { console.debug("[address-history] push remote failed (offline / non-admin):", e?.message || e); }
}

export async function removeAddressHistory(address) {
  const key = (address || "").toLowerCase().trim();
  const current = _cache || _readLocal();
  const next = current.filter((p) => (p.address || "").toLowerCase().trim() !== key);
  _cache = next;
  _writeLocal(next);
  try { await api.post("/address-history/delete", { address }); } catch (e) { console.debug("[address-history] remove remote failed:", e?.message || e); }
}

export async function clearAddressHistory() {
  _cache = [];
  _writeLocal([]);
  try { await api.delete("/address-history"); } catch (e) { console.debug("[address-history] clear remote failed:", e?.message || e); }
}

/** Sync filter on cached list — case-insensitive substring over name/address/city/postal_code. */
export function filterCachedAddressHistory(query) {
  const list = getCachedAddressHistory();
  const q = (query || "").toLowerCase().trim();
  if (!q) return list;
  return list.filter((p) => (
    (p.address || "").toLowerCase().includes(q) ||
    (p.name || "").toLowerCase().includes(q) ||
    (p.city || "").toLowerCase().includes(q) ||
    (p.postal_code || "").includes(q)
  ));
}
